import React, { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControl } from "@primer/react";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import Modal from "../../components/ui/modal";
import ModalActions from "../../components/ui/modal/ModalActions";
import Button from "../../components/ui/button";
import TextInput from "../../components/ui/text-input";
import MyHtmlEditor from "../../components/html-editor";
import SccopeSelectionButtonByLop from "../../components/scope-selection-button/SccopeSelectionButtonByLop";
import { NotifyHelper } from "../../helpers/toast";
import { IClubPeriodVm } from "../../models/response/club/IClubPeriodVm";
import { clubApi } from "../../api/clubApi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface Props {
    isOpen: boolean;
    id: number;
    onClose: () => void;
    onSuccess?: () => void;
}

const ClubPeriodDetailModal = ({ isOpen, id, onClose, onSuccess }: Props) => {
    const fake: any = {};
    const [clubPeriod, setClubPeriod] = useState<IClubPeriodVm>(fake);
    const { nam_hoc } = useCommonSelectedHook();
    const { register, handleSubmit, reset, clearErrors, setError, control, formState: { errors } } = useForm<IClubPeriodVm>({
        mode: "all"
    });

    const lopIdsSelected = useMemo(() => {
        return clubPeriod?.scopes?.map(x => x.dm_lop_id) ?? [];
    }, [clubPeriod]);

    useEffect(() => {
        if (id) handleGetDataAsync();
    }, [id]);

    const handleGetDataAsync = async () => {
        const res = await clubApi.periodSelectDetail(id);
        if (res.is_success) {
            setClubPeriod(res.data);
            reset({
                ...res.data,
                registration_end_at: res.data?.registration_end_at ? moment(res.data.registration_end_at).format("YYYY-MM-DD HH:mm") : undefined,
                registration_start_at: res.data?.registration_start_at ? moment(res.data.registration_start_at).format("YYYY-MM-DD HH:mm") : undefined,
            });
        }
    };

    const onSubmit = async (data: any) => {
        let isValid = true;

        if ((clubPeriod?.scopes ?? []).length <= 0) {
            isValid = false;
            setError("scopes", {});
        }

        if (!isValid) return;

        const payload = {
            ...data,
            registration_start_at: data.registration_start_at
                ? moment(data.registration_start_at).format("YYYY-MM-DDTHH:mm:ss")
                : null,
            registration_end_at: data.registration_end_at
                ? moment(data.registration_end_at).format("YYYY-MM-DDTHH:mm:ss")
                : null,
        };

        const res = await clubApi.periodSaveChange({
            ...clubPeriod,
            ...payload,
            scopes: clubPeriod.scopes,
            id: id,
            nam_hoc: nam_hoc
        });

        if (res.is_success) {
            NotifyHelper.Success("Success");
            onSuccess?.();
            onClose();
        } else {
            NotifyHelper.Error(res.message ?? "Error");
        }
    };

    return (
        <Modal
            width="xlarge"
            isOpen={isOpen}
            title="Chi tiết đợt đăng ký"
            onClose={onClose}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: "grid", gap: 3 }}>
                    <FormControl>
                        <FormControl.Label>Tên đợt đăng ký</FormControl.Label>
                        <TextInput
                            register={register}
                            name="name"
                            required
                            width={'100%'}
                        />
                        {
                            errors["name"] &&
                            <FormControl.Validation variant="error">
                                Vui lòng nhập tên đợt đăng ký
                            </FormControl.Validation>
                        }
                    </FormControl>
                    <FormControl>
                        <FormControl.Label>Tên đợt đăng ký EN</FormControl.Label>
                        <TextInput
                            register={register}
                            name="name_en"
                            required
                            width={'100%'}
                        />
                        {
                            errors["name_en"] &&
                            <FormControl.Validation variant="error">
                                Vui lòng nhập tên đợt đăng ký en
                            </FormControl.Validation>
                        }
                    </FormControl>
                    <FormControl>
                        <FormControl.Label>Số lượng tối đa CLB học sinh được đăng ký</FormControl.Label>
                        <TextInput
                            register={register}
                            name="quantity_max"
                            required
                            type="number"
                        />
                        {
                            errors["quantity_max"] &&
                            <FormControl.Validation variant="error">
                                Vui lòng nhập số lượng tối đa
                            </FormControl.Validation>
                        }
                    </FormControl>
                    {/* Scope */}
                    <FormControl>
                        <FormControl.Label>Phạm vi</FormControl.Label>
                        <SccopeSelectionButtonByLop
                            lopIdsSelected={lopIdsSelected}
                            onValueChanged={(ids: number[]) => {
                                setClubPeriod({
                                    ...clubPeriod,
                                    scopes: ids.map(lopId => ({
                                        id: 0,
                                        sis_club_registration_period_id: id,
                                        dm_lop_id: lopId
                                    }))
                                });
                                if (ids.length > 0) clearErrors("scopes");
                            }}
                        />
                        {
                            errors["scopes"] &&
                            <FormControl.Validation variant="error">
                                Vui lòng chọn lớp
                            </FormControl.Validation>
                        }
                    </FormControl>

                    {/* Time đăng ký */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <FormControl>
                            <FormControl.Label>Đăng ký từ</FormControl.Label>
                            <TextInput
                                type="datetime-local"
                                name="registration_start_at"
                                register={register}
                                required
                                errors={errors}
                                validateMessage='Vui lòng điền thời gian bắt đầu đăng ký'

                            />
                        </FormControl>

                        <FormControl>
                            <FormControl.Label>Đăng ký đến</FormControl.Label>
                            <TextInput
                                type="datetime-local"
                                name="registration_end_at"
                                register={register}
                                required
                                errors={errors}
                                validateMessage='Vui lòng điền thời gian kết thúc đăng ký'

                            />
                        </FormControl>
                    </Box>

                    {/* Checkbox */}
                    <FormControl>
                        <FormControl.Label>Publish</FormControl.Label>
                        <Checkbox
                            checked={clubPeriod?.is_published ?? false}
                            onChange={(e) => {
                                setClubPeriod({
                                    ...clubPeriod,
                                    is_published: e.target.checked
                                });
                            }}
                        />
                    </FormControl>
                </Box>

                <ModalActions>
                    <Button text="Đóng" onClick={onClose} />
                    <Button text={id > 0 ? "Cập nhật" : "Thêm mới"} type="submit" variant="primary" />
                </ModalActions>
            </form>
        </Modal>
    );
};

export default ClubPeriodDetailModal;